import React from 'react';
import { Offcanvas, Button, Table } from 'react-bootstrap';
import { X as FiX } from 'react-bootstrap-icons';
import { Quote } from '../../models/quote';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import { getQuoteReference } from './quoteReference';
import styles from './Quotations.module.scss';

interface QuotationViewProps {
  show: boolean;
  onClose: () => void;
  quote: Quote | null;
  referenceCurrency?: string;
  onConvert?: (quote: Quote) => void;
  onEdit?: (quote: Quote) => void;
}

function formatDate(date: any): string {
  if (!date) return 'N/A';
  try {
    let dateObj: Date;
    if (date && typeof date === 'object') {
      if (typeof date.toDate === 'function') {
        dateObj = date.toDate();
      } else if ('seconds' in date) {
        dateObj = new Date((date as any).seconds * 1000);
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        dateObj = new Date(date);
      }
    } else {
      dateObj = new Date(date);
    }
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
}

const QuotationView: React.FC<QuotationViewProps> = ({
  show,
  onClose,
  quote,
  referenceCurrency = 'USD',
  onConvert,
  onEdit,
}) => {
  if (!quote) return null;

  const subtotal = quote.subtotal || 0;
  const storedIva = quote.ivaAmount || 0;
  const inferredIva = (quote.total || 0) - subtotal;
  const ivaAmount =
    Math.abs(subtotal + storedIva - (quote.total || 0)) > 0.01
      ? inferredIva
      : storedIva;
  const total = quote.total || 0;
  const exchangeRate = quote.exchangeRate || 1;

  const getCurrencySymbol = (code?: string): string => {
    if (!code) return '';
    return SUPPORTED_CURRENCIES.find(c => c.code === code)?.symbol || code;
  };
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const money = (amount: number) => {
    const symbol = getCurrencySymbol(referenceCurrency);
    const rounded = numberFormatter.format(Number(amount) || 0);
    return symbol ? `${symbol} ${rounded}` : rounded;
  };

  const reference = getQuoteReference(quote);

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      scroll={false}
      backdrop
      className={styles.drawer}
      backdropClassName={styles.drawerBackdrop}
    >
      <div className={styles.drawerForm}>
        <Offcanvas.Header className={styles.header}>
          <div className={styles.headerRow}>
            <Offcanvas.Title className={styles.headerTitle}>
              Cotización {reference}
            </Offcanvas.Title>
            <button
              type="button"
              className={styles.headerClose}
              onClick={onClose}
              aria-label="Cerrar"
            >
              <FiX size={22} aria-hidden />
            </button>
          </div>
        </Offcanvas.Header>

        <Offcanvas.Body className={styles.body}>
          <div className={styles.viewTop}>
            <div>
              <p className={styles.viewRef}>Cotización {reference}</p>
              <h3 className={styles.viewClientName}>{quote.clientName}</h3>
            </div>
            <div className={styles.viewTotalBlock}>
              <span className={styles.viewTotalLabel}>Total</span>
              <span className={styles.viewTotalValue}>{money(total)}</span>
            </div>
          </div>

          <div className={styles.viewMeta}>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Vendedor</span>
              <span className={styles.viewMetaValue}>
                {quote.creatorSalesPerson || '—'}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Creada</span>
              <span className={styles.viewMetaValue}>
                {formatDate(quote.created)}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Pago</span>
              <span className={styles.viewMetaValue}>
                {quote.paymentMethod || '—'}
                {quote.paymentCurrency ? ` · ${quote.paymentCurrency}` : ''}
              </span>
            </div>
            <div className={styles.viewMetaItem}>
              <span className={styles.viewMetaLabel}>Tasa</span>
              <span className={styles.viewMetaValue}>
                {formatExchangeRate(exchangeRate)}
              </span>
            </div>
            <div className={`${styles.viewMetaItem} ${styles.viewMetaWide}`}>
              <span className={styles.viewMetaLabel}>Dirección</span>
              <span className={styles.viewMetaValue}>
                {quote.shippingAddress || '—'}
              </span>
            </div>
          </div>

          {quote.comment ? (
            <p className={styles.viewComment}>{quote.comment}</p>
          ) : null}

          <Table responsive hover size="sm" className={styles.viewTable}>
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th className="text-end">Cant.</th>
                <th className="text-end">Precio</th>
                <th className="text-end">Desc. %</th>
                <th className="text-end">Importe</th>
              </tr>
            </thead>
            <tbody>
              {(quote.products || []).map((p, idx) => {
                const qty = Number(p.quantity) || 0;
                const price = Number(p.price) || 0;
                const discount = Number(p.discount) || 0;
                const line = price * qty * (1 - discount / 100);
                return (
                  <tr key={`${p.productId}-${idx}`}>
                    <td>{p.code}</td>
                    <td>{p.name}</td>
                    <td className="text-end">{qty}</td>
                    <td className="text-end text-nowrap">{money(price)}</td>
                    <td className="text-end">{discount}</td>
                    <td className="text-end text-nowrap">{money(line)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className={styles.viewSummary}>
            <div className={styles.totalsRow}>
              <span>Subtotal</span>
              <span className="text-nowrap">{money(subtotal)}</span>
            </div>
            <div className={styles.totalsRow}>
              <span>IVA</span>
              <span className="text-nowrap">{money(ivaAmount)}</span>
            </div>
            <div className={`${styles.totalsRow} ${styles.totalsRowTotal}`}>
              <span>Total</span>
              <span className="text-nowrap">{money(total)}</span>
            </div>
          </div>
        </Offcanvas.Body>

        <div className={styles.footer}>
          <div className={styles.footerActions}>
            <Button variant="outline-secondary" onClick={onClose}>
              Cerrar
            </Button>
            {onEdit && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  onEdit(quote);
                  onClose();
                }}
              >
                Editar
              </Button>
            )}
            {onConvert && (
              <Button variant="success" onClick={() => onConvert(quote)}>
                Convertir a venta
              </Button>
            )}
          </div>
        </div>
      </div>
    </Offcanvas>
  );
};

export default QuotationView;
